import React, { useEffect, useState } from "react";
import {
  FaCog,
  FaChartLine,
  FaFileAlt,
  FaWallet,
  FaUniversity,
} from "react-icons/fa";
import { getEmail, getRole } from "../../../DecodeToken/DecodeToken";
import {
  getBankAccountDetails,
  getBookingSummary,
  getTransactionsHistory,
  getBookingAmount as getBookingAmount,
  getWithdrawalHistory,
  getUpiDetails,
} from "../../../ApiServices/ApiServices";
import BankAccountDetails from "./BankAccountDetails";
import WithdrawService from "./WithdrawRequest";
import { useAuth } from "../../../provider/authProvider";
import CodeQueueSpinner, { handleError } from "../../../Utils";
import UserUPIID from "../Rewards/UserUPIID";

const Payments = () => {
  const [activeButton, setActiveButton] = useState(1); // Set default active button to Summary (id: 1)
  const email = getEmail();
  const role = getRole();
  const [bookingsAmount, setBookingsAmount] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  // const [isBankAccountConnected, setIsBankAccountConnected] = useState(false);

  const [summary, setSummary] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdraw, setWithdraw] = useState([]);

  const [withdrawRequestVisible, setWithdrawRequestVisible] = useState(false);
  const balanceToWithdraw = bookingsAmount?.balance ?? 0;
  const { getUserName } = useAuth();
  const name = getUserName();
  const [lastRequestedWithdrawalAmount, setLastRequestedWithdrawalAmount] =
    useState(0);
  const [lastRequestedWithdrawalStatus, setLastRequestedWithdrawalStatus] =
    useState("");
  const [loading, setLoading] = useState(true);

  const [upiId, setUpiId] = useState("");
  const [userName, setUserName] = useState("");
  const [isUPIConnected, setIsUPIConnected] = useState(false);


  const fetchUPIDetails = async () => {
      try {
        const res = await getUpiDetails();
        setUpiId(res?.upiData?.upi_id);
        setUserName(res?.upiData?.upi_user);
        if (res?.upiData?.upi_id) {
          setIsUPIConnected(true);
        }
      } catch (error) {
        return handleError(error?.response?.data?.message);
      }
    };

  //get wthdraw history
  const getPaymentDetails = async () => {
    const res = await getBookingAmount(email);
    setBookingsAmount(res.data);

    // check if UPI has been added or not 
    await fetchUPIDetails();

    // check if bank account details has updated.
    // const response = await getBankAccountDetails(email);
    // if (response.data) {
    //   setIsBankAccountConnected(true);
    // }

    // get booking summary
    const summary = await getBookingSummary(email);
    setSummary(summary);

    //get transactions history
    const transactions = await getTransactionsHistory(email);
    setTransactions(transactions.data);

    // get withdrawal history
    const withdraw = await getWithdrawalHistory(email);
    withdraw?.data?.map((item) => {
      if (item?.requestStatus === "pending") {
        setLastRequestedWithdrawalAmount(item?.requestAmount);
        setLastRequestedWithdrawalStatus(item?.requestStatus);
      }
    });
    setWithdraw(withdraw.data);
  };

  useEffect(() => {
    document.title = "Dashboard - Payments";
    try {
      if (role === "mentor") {
        if (email) {
          getPaymentDetails();
        }
      }
    } catch (error) {
      return handleError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleButtonClick = (buttonId) => {
    setActiveButton(buttonId);
  };
  const handleWithdrawClick = () => {
    if (isUPIConnected) {
      setWithdrawRequestVisible(!withdrawRequestVisible);
    } else {
      setIsVisible(!isVisible);
    }
  };

  // Button configuration
  const buttons = [
    { id: 1, label: "Summary" },
    { id: 2, label: "Transactions" },
    { id: 3, label: "Withdrawals" },
  ];

  return (
    <div className="px-2 py-2">
      {/* Main Heading Section */}
      <div className="flex flex-wrap justify-between items-start border-b border-gray-400">
        <div className="text-2xl font-bold text-black">Payments</div>
        {isUPIConnected ? (
          <div
            className="flex items-center mt-2 sm:mt-0 cursor-pointer hover:bg-slate-300"
            onClick={setIsVisible}
          >
            <div className="flex items-center space-x-2 border-l border-gray-400 p-2">
              <FaUniversity className="w-8 h-8 flex items-center justify-center" />
              <div>
                <div className="flex items-center space-x-1">
                  <span className="text-sm">Payout</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="text-green-500 text-sm">Connected</div>
                  <div className="w-4 h-4 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
                    ✔
                  </div>
                </div>
              </div>
            </div>
            <FaCog className="w-10 h-10 rounded-full border flex items-center justify-center ml-2 p-2" />
          </div>
        ) : (
          <div className="mt-2 mb-4">
            <a
              onClick={setIsVisible}
              className="bg-black text-white py-2 px-10 rounded-lg hover:cursor-pointer"
            >
              Payout
            </a>
          </div>
        )}
      </div>

      {/* Middle Section (Three Columns Layout) */}
      {loading ? (
        <p className="mt-4"><CodeQueueSpinner /></p>
      ) : (
        <div className="mt-4">
          <div className="w-full bg-violet-50 border border-transparent rounded-lg p-2 md">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                {
                  icon: <FaWallet className="w-8 h-8 flex" />,
                  label: "Balance",
                  value: "₹" + (bookingsAmount?.balance ?? 0),
                  info: "Your current wihtdrawable balance + CodeQueue commission",
                },
                {
                  icon: <FaChartLine className="w-8 h-8 flex" />,
                  label: "Lifetime Earnings",
                  value: "₹" + (bookingsAmount?.lifetimeEarnings ?? 0),
                  info: "Lifetime Earnings on CodeQueue",
                },
                {
                  icon: <FaFileAlt className="w-8 h-8 flex" />,
                  label: "Pending Call Completion",
                  value: "₹" + (bookingsAmount?.pendingCallBalance ?? 0),
                  info: "Earnings from pending calls will move to balance once completed.",
                },
              ].map(({ icon, label, value, info }, index) => (
                <div
                  key={index}
                  className={`p-4 ${
                    index === 1
                      ? "sm:border-r-0 md:border-r-2 md:border-dashed md:border-gray-300 "
                      : ""
                  } ${index === 0 ? "bg-[#F7F4E9]" : "bg-violet-50"} relative`}
                >
                  <div className="flex items-center mt-3">
                    {icon}
                    {index === 0 && (
                      <span className="ml-32 font-bold text-lg text-black">
                        25% Commission(Fees)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center mt-2">
                    <span className="text-left text-lg text-black">{label}</span>
                    <div className="relative group ml-4">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 text-gray-700 text-xs font-bold cursor-pointer">
                        i
                      </span>
                      <div
                        className="absolute left-4 bottom-full mb-2 z-20 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-2 whitespace-normal shadow-lg"
                        style={{ width: "200px", minHeight: "32px" }}
                      >
                        {info}
                      </div>
                    </div>
                  </div>
                  <p className="text-2xl text-left font-semibold text-gray-700 mb-2">
                    {value}
                  </p>
                  {index === 0 && (
                    <div className="absolute bottom-2 right-2">
                      {lastRequestedWithdrawalStatus === "pending" ? (
                        <div className="text-yellow-700">
                          In Progress : ₹
                          <span>{lastRequestedWithdrawalAmount}</span>
                        </div>
                      ) : (
                        parseFloat(value.replace("₹", "")) > 0 && (
                          <button
                            className="bg-black text-white font-bold py-1 rounded-lg border-2 text-sm px-2 w-full"
                            onClick={handleWithdrawClick}
                          >
                            Withdraw
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Third Section - Buttons and Table */}
      <div className="mt-6">
        {/* Button Row */}
        <div className="flex flex-wrap justify-start gap-1 mb-4">
          {buttons.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => handleButtonClick(id)}
              className={` py-2 rounded-lg font-semibold text-sm transition-all md:w-[120px] w-[120px] text-center ${
                activeButton === id
                  ? "border-2 border-black text-black"
                  : "border-2 border-gray-300 text-gray-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Conditional Content Rendering */}
        {activeButton === 1 && (
          <div
            className="overflow-y-auto max-h-64 scrollbar-none"
            style={{
              scrollbarWidth: "none", // For Firefox
              msOverflowStyle: "none", // For Internet Explorer and Edge
            }}
          >
            <table className="w-full table-auto  text-sm border border-gray-300 ">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left font-semibold text-black border-b border-r border-gray-300">
                    Name
                  </th>
                  <th className="p-2 text-left font-semibold text-black border-b border-r border-gray-300">
                    Count
                  </th>
                  <th className="p-2 text-left font-semibold text-black border-b border-gray-300">
                    Earnings
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.map((summary, index) => (
                  <tr key={index}>
                    <td className="p-2 bg-gray-50 border-b border-r border-gray-300">
                      {summary.session_title}
                    </td>
                    <td className="p-2 border-b border-r border-gray-300">
                      {summary.count}
                    </td>
                    <td className="p-2 border-b border-gray-300">
                      {summary.totalAmount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeButton === 2 && (
          <div className="border border-gray-300 rounded">
            {/* Fixed Header */}
            <div className="sticky top-0 bg-gray-100 z-10 border-b border-gray-300">
              <div className="flex">
                <div className="flex-1 text-left font-semibold text-black border-r border-gray-300 p-2">
                  Type
                </div>
                <div className="w-1/4 text-left font-semibold text-black border-r border-gray-300 p-2">
                  Date
                </div>
                <div className="flex-1 text-left font-semibold text-black border-r border-gray-300 p-2">
                  Customer
                </div>
                <div className="w-20 text-left font-semibold text-black p-2">
                  ₹
                </div>
              </div>
            </div>
            
            {/* Scrollable Body */}
            <div 
              className="overflow-y-auto max-h-64 scrollbar-none"
              style={{
                scrollbarWidth: "none", // For Firefox
                msOverflowStyle: "none", // For Internet Explorer and Edge
              }}
            >
              {transactions?.map((transaction, index) => {
                const statusColor =
                  transaction.status === "completed"
                    ? "green"
                    : transaction.status === "cancelled"
                    ? "red"
                    : transaction.status === "answered"
                    ? "green"
                    : transaction.status === "referred"
                    ? "green"
                    : "yellow"; 
                return (
                  <div key={index} className="flex border-b border-gray-300 hover:bg-gray-50">
                    <div className="flex-1 p-2 bg-gray-50 border-r border-gray-300">
                      <div className="font-medium">{transaction.session_title || transaction.type}</div>
                      <div className={`text-sm text-${statusColor}-500 mt-1`}>
                        {transaction.status}
                      </div>
                    </div>
                    <div className="w-1/4 p-2 border-r border-gray-300">
                      {transaction.date}
                    </div>
                    <div className="flex-1 p-2 border-r border-gray-300">
                      {transaction.user_name}
                    </div>
                    <div className="w-20 p-2 font-medium">
                      {transaction.totalAmount}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {activeButton === 3 && (
          <div className="border border-gray-300 rounded">
            {/* Fixed Header */}
            <div className="sticky top-0 bg-gray-100 z-10 border-b border-gray-300">
              <div className="flex">
                <div className="flex-1 text-left font-semibold text-black border-r border-gray-300 p-2">
                  Date
                </div>
                <div className="flex-1 text-left font-semibold text-black border-r border-gray-300 p-2">
                  Withdrawal Amount
                </div>
                <div className="flex-1 text-left font-semibold text-black p-2">
                  Status
                </div>
              </div>
            </div>
            
            {/* Scrollable Body */}
            <div 
              className="overflow-y-auto max-h-64 scrollbar-none"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {withdraw.map((withdrawal, index) => (
                <div key={index} className="flex border-b border-gray-300 hover:bg-gray-50">
                  <div className="flex-1 p-2 border-r border-gray-300">
                    {new Date(withdrawal?.requestDate).toLocaleDateString("en-US", {
                      day: "2-digit",
                      month: "short", 
                      year: "numeric",
                    })}
                  </div>
                  <div className="flex-1 p-2 border-r border-gray-300 font-medium">
                    ₹{withdrawal?.requestAmount}
                  </div>
                  <div className={`flex-1 p-2 font-medium ${
                    withdrawal?.requestStatus === "pending"
                      ? "text-yellow-600"
                      : "text-green-600"
                  }`}>
                    {withdrawal?.requestStatus}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* popup for bank account details  */}
      {/* {isVisible && (
        <BankAccountDetails isVisible={isVisible} setIsVisible={setIsVisible} setIsBankAccountConnected={setIsBankAccountConnected}/>
      )} */}

      {/* popup for UPI id */}
      {isVisible && (
        <UserUPIID
          setIsVisible={setIsVisible}
          setIsUPIConnected={setIsUPIConnected}
          upiId={upiId}
          setUpiId={setUpiId}
          userName={userName}
          setUserName={setUserName}
        />
      )}
      {withdrawRequestVisible && (
        <WithdrawService
          setWithdrawRequestVisible={setWithdrawRequestVisible}
          balanceToWithdraw={balanceToWithdraw}
          name={name}
          getPaymentDetails={getPaymentDetails}
        />
      )}
    </div>
  );
};

export default Payments;
